// === Two-factor authentication (TOTP) — self-enrolment ===
//
// Enrolment can ONLY be done by the account's owner. supabase.auth.mfa.enroll()
// acts on the CURRENT session and returns a QR containing the TOTP secret —
// which is exactly why an admin cannot do this for someone else. If a QR for
// your account could be produced on my screen, I would be holding your second
// factor. An admin's only MFA power over another user is REMOVING their factor
// (lost phone), which lives on the Users page.
//
// Flow: enroll() -> show QR -> user scans -> types the 6-digit code ->
// challengeAndVerify() marks the factor 'verified' AND raises this session to
// aal2. Until that verify step the factor is 'unverified' and protects nothing,
// so the API deliberately ignores unverified factors.
//
// NOTE: the API rejects a password-only (aal1) session for any superadmin who
// has a verified factor — see verifySuperadmin in apps/api/src/lib/auth.ts. So
// enrolling here makes the login challenge mandatory from the next sign-in on.

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField, Typography, Chip,
} from '@mui/material';
import { Security as SecurityIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { getSupabaseClient } from '../lib/auth';

interface Factor {
  readonly id: string;
  readonly status: string;
  readonly friendly_name?: string | undefined;
}

export function MfaSection() {
  const [factors, setFactors] = useState<readonly Factor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Enrolment dialog
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const verified = factors.find((f) => f.status === 'verified');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = await getSupabaseClient();
      const { data, error: listErr } = await supabase.auth.mfa.listFactors();
      if (listErr) throw listErr;
      setFactors((data?.all ?? []) as Factor[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read two-factor status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const startEnrol = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const supabase = await getSupabaseClient();

      // Clear any half-finished enrolment first — Supabase rejects a second
      // enroll() while an unverified factor is outstanding, which otherwise
      // leaves you permanently unable to start again.
      for (const f of factors.filter((x) => x.status !== 'verified')) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }

      const { data, error: enrolErr } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}`,
      });
      if (enrolErr) throw enrolErr;
      setQr(data?.totp?.qr_code ?? null);
      setSecret(data?.totp?.secret ?? null);
      setFactorId(data?.id ?? null);
      setCode('');
      setEnrolOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start enrolment.');
    } finally {
      setBusy(false);
    }
  }, [factors]);

  const confirmEnrol = useCallback(async () => {
    if (!factorId || code.trim().length < 6) return;
    setBusy(true);
    setError(null);
    try {
      const supabase = await getSupabaseClient();
      const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.trim(),
      });
      if (verifyErr) throw verifyErr;
      setEnrolOpen(false);
      setSuccess('Two-factor authentication is on. You will be asked for a code at your next sign-in.');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That code was not accepted. Check your phone clock and try again.');
    } finally {
      setBusy(false);
    }
  }, [factorId, code, refresh]);

  const removeFactor = useCallback(async () => {
    if (!verified) return;
    setBusy(true);
    setError(null);
    try {
      const supabase = await getSupabaseClient();
      const { error: unErr } = await supabase.auth.mfa.unenroll({ factorId: verified.id });
      if (unErr) throw unErr;
      setSuccess('Two-factor authentication removed. Your account is password-only again.');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the factor.');
    } finally {
      setBusy(false);
    }
  }, [verified, refresh]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <SecurityIcon color="action" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>Two-Factor Authentication</Typography>
        {verified ? <Chip size="small" color="success" icon={<CheckIcon />} label="On" /> : null}
      </Box>

      {success !== null ? (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>
      ) : null}
      {error !== null ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>
      ) : null}

      {loading ? (
        <CircularProgress size={20} />
      ) : verified ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your account asks for a 6-digit code at sign-in. As a superadmin you can reach every
            customer's data, so a password on its own is not enough.
          </Typography>
          <Button size="small" color="error" variant="outlined" onClick={() => void removeFactor()} disabled={busy}>
            Turn off two-factor
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Add a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy) to
            your sign-in. Strongly recommended — this account can reach every customer's data.
          </Typography>
          <Button size="small" variant="contained" onClick={() => void startEnrol()} disabled={busy}>
            {busy ? 'Preparing…' : 'Set up two-factor'}
          </Button>
        </Box>
      )}

      {/* Enrolment dialog — QR + confirm */}
      <Dialog open={enrolOpen} onClose={() => setEnrolOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Scan this with your authenticator app</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {qr !== null ? (
              // Supabase returns the QR as an SVG data URI.
              <Box component="img" src={qr} alt="Two-factor QR code" sx={{ width: 200, height: 200 }} />
            ) : <CircularProgress />}

            {secret !== null ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Can't scan? Enter this key manually:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {secret}
                </Typography>
              </Box>
            ) : null}

            <TextField
              label="6-digit code"
              fullWidth
              size="small"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              helperText="Enter the code your app shows now to finish setup"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEnrolOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirmEnrol()} disabled={busy || code.length < 6}>
            {busy ? 'Verifying…' : 'Turn on'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
